import { ArrowLeft, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useFeeds,
  usePlaylists,
  useSchedules,
  useSlots,
} from "../../api/hooks.js";
import { patchFeed } from "../../api/feeds.js";
import { replaceSlots, type SlotInput } from "../../api/programming.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { Switch } from "../../design-system/form.js";
import { Notice, Panel, StateSurface } from "../../design-system/surfaces.js";
import { useNow } from "../../lib/use-now.js";
import { PageHeading } from "../shared/page-heading.js";
import {
  DAY_ABBREVIATIONS,
  describeSlot,
  governingSlotAt,
  layoutWeek,
  trimTime,
} from "./grid-model.js";
import { SlotDialog, type SlotDraft } from "./slot-dialog.js";
import { feedsUsingSchedule } from "./usage.js";
import { toneAssignment, WeekGrid } from "./week-grid.js";
import type { ScheduleSlot } from "../../api/types.js";

/**
 * One schedule, as a week.
 *
 * Slots are replaced whole, like playlist items and for the same reason: the API's
 * verb is a replacement, so every change here is one request that either takes or does
 * not, and a refusal puts the week back to what the API holds.
 *
 * The grid is drawn from the slots alone, by the same rules the brain reads them by —
 * a slot is anchored to the day it starts on, an end at or before the start runs
 * through midnight, and where several cover an hour the highest priority governs. What
 * is on the grid is what will play.
 */
export function SchedulePage() {
  const { scheduleId = "" } = useParams();
  const schedules = useSchedules(true);
  const playlists = usePlaylists(true);
  const feeds = useFeeds(true);
  const stored = useSlots(scheduleId, true);

  const [slots, setSlots] = useState<ScheduleSlot[]>([]);
  const [editing, setEditing] = useState<SlotDraft | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [day, setDay] = useState(() => new Date().getDay());
  const [assigning, setAssigning] = useState<string | null>(null);

  useEffect(() => {
    setSlots(stored.data ?? []);
  }, [stored.data]);

  const schedule = (schedules.data ?? []).find(
    (entry) => entry.id === scheduleId,
  );
  const now = useNow(60_000, schedule !== undefined);
  const nowMarker = useMemo(
    () => (schedule ? localNow(now, schedule.timezone) : null),
    [now, schedule],
  );

  const blocks = useMemo(() => layoutWeek(slots), [slots]);
  const playlistName = useMemo(() => {
    const names = new Map(
      (playlists.data ?? []).map((playlist) => [playlist.id, playlist.name]),
    );
    return (playlistId: string) => names.get(playlistId) ?? "Unknown playlist";
  }, [playlists.data]);

  // The tone a playlist draws in is decided per schedule, in the order its slots
  // name playlists, so the first two are always the furthest apart the rotation goes.
  const toneOf = useMemo(
    () => toneAssignment(slots.map((slot) => slot.playlistId)),
    [slots],
  );

  const governing = useMemo(
    () =>
      nowMarker
        ? governingSlotAt(slots, nowMarker.day, nowMarker.seconds)
        : null,
    [nowMarker, slots],
  );

  function persist(next: ScheduleSlot[]) {
    const previous = slots;
    setSlots(next);
    setSaving(true);
    setError(null);
    replaceSlots(
      scheduleId,
      next.map((slot): SlotInput => ({
        ...(slot.id.startsWith("pending-") ? {} : { id: slot.id }),
        playlist_id: slot.playlistId,
        days_of_week: slot.daysOfWeek,
        starts_at: slot.startsAt,
        ends_at: slot.endsAt,
        priority: slot.priority,
        enabled: slot.enabled,
      })),
    )
      .then((saved) => {
        setSlots(saved);
      })
      .catch((cause: unknown) => {
        setSlots(previous);
        setError(
          cause instanceof Error ? cause.message : "The week was not saved.",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function saveSlot(draft: SlotDraft) {
    const shaped: ScheduleSlot = {
      id: draft.id ?? `pending-${String(slots.length)}-${draft.playlistId}`,
      scheduleId,
      playlistId: draft.playlistId,
      daysOfWeek: draft.daysOfWeek,
      startsAt: draft.startsAt,
      endsAt: draft.endsAt,
      priority: draft.priority,
      enabled: draft.enabled,
    };
    persist(
      draft.id
        ? slots.map((slot) => (slot.id === draft.id ? shaped : slot))
        : [...slots, shaped],
    );
  }

  function assign(feedId: string, uses: boolean) {
    setAssigning(feedId);
    setError(null);
    patchFeed(feedId, { schedule_id: uses ? scheduleId : null })
      .then(() => {
        feeds.reload();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "That feed's schedule was not changed.",
        );
      })
      .finally(() => {
        setAssigning(null);
      });
  }

  if (schedules.loading || stored.loading) {
    return <StateSurface kind="loading" title="Reading this schedule" />;
  }
  if (!schedule) {
    return (
      <StateSurface
        action={
          <Link
            className="text-sm font-semibold text-accent-text"
            to="/schedules"
          >
            Back to schedules
          </Link>
        }
        kind="error"
        title="This schedule could not be read"
      >
        {schedules.error ?? "No schedule has that address."}
      </StateSurface>
    );
  }

  const assigned = feedsUsingSchedule(schedule.id, feeds.data ?? []);

  return (
    <div className="flex flex-col gap-6">
      <Link
        className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-ink-3 transition-colors hover:text-ink"
        to="/schedules"
      >
        <ArrowLeft aria-hidden="true" className="size-3.5" />
        Schedules
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading
          subtitle={`${schedule.timezone} · ${String(slots.length)} ${slots.length === 1 ? "slot" : "slots"}${schedule.enabled ? "" : " · disabled"}`}
          title={schedule.name}
        />
        <Button
          onClick={() => {
            setEditing({
              playlistId: "",
              daysOfWeek: [day],
              startsAt: "06:00",
              endsAt: "10:00",
              priority: 0,
              enabled: true,
            });
            setDialogOpen(true);
          }}
        >
          <Plus aria-hidden="true" />
          New slot
        </Button>
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-3">
              {governing
                ? `Now: ${playlistName(governing.playlistId)}`
                : "Nothing scheduled. Feeds play their default playlist."}
            </p>
            <p
              aria-live="polite"
              className="font-mono text-[0.6875rem] text-ink-3"
            >
              {saving ? "Saving the week…" : ""}
            </p>
          </div>

          {/* Seven columns at 1440; one chosen day at 360, because seven columns in
              360 pixels is seven columns of nothing. */}
          <div className="flex gap-1 overflow-x-auto lg:hidden">
            {DAY_ABBREVIATIONS.map((label, index) => (
              <button
                key={label}
                aria-pressed={day === index}
                className={cn(
                  "min-h-9 flex-1 rounded-sm border px-2 text-xs font-medium transition-colors",
                  day === index
                    ? "border-transparent bg-accent text-accent-ink"
                    : "border-border-hi bg-surface text-ink-2",
                )}
                onClick={() => {
                  setDay(index);
                }}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="hidden lg:block">
            <WeekGrid
              blocks={blocks}
              days={[0, 1, 2, 3, 4, 5, 6]}
              nowMarker={nowMarker}
              onCreate={(created, seconds) => {
                setEditing({
                  playlistId: "",
                  daysOfWeek: [created],
                  startsAt: secondsToInput(seconds),
                  endsAt: secondsToInput(seconds + 3_600),
                  priority: 0,
                  enabled: true,
                });
                setDialogOpen(true);
              }}
              onOpen={(slotId) => {
                openSlot(slotId);
              }}
              playlistName={playlistName}
              toneOf={toneOf}
            />
          </div>
          <div className="lg:hidden">
            <WeekGrid
              blocks={blocks}
              days={[day]}
              nowMarker={nowMarker}
              onCreate={(created, seconds) => {
                setEditing({
                  playlistId: "",
                  daysOfWeek: [created],
                  startsAt: secondsToInput(seconds),
                  endsAt: secondsToInput(seconds + 3_600),
                  priority: 0,
                  enabled: true,
                });
                setDialogOpen(true);
              }}
              onOpen={(slotId) => {
                openSlot(slotId);
              }}
              playlistName={playlistName}
              toneOf={toneOf}
            />
          </div>

          {slots.length === 0 ? (
            <p className="text-xs leading-5 text-ink-3">
              No slot yet. Click any hour on the grid to say which playlist
              governs it.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {[...slots]
                .sort(
                  (left, right) =>
                    right.priority - left.priority ||
                    left.startsAt.localeCompare(right.startsAt),
                )
                .map((slot) => (
                  <li key={slot.id}>
                    <button
                      className="flex w-full items-center gap-3 rounded-sm border border-border bg-surface px-3 py-2 text-left transition-colors hover:border-accent-line"
                      onClick={() => {
                        openSlot(slot.id);
                      }}
                      type="button"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">
                        {playlistName(slot.playlistId)}
                      </span>
                      <span className="shrink-0 font-mono text-[0.6875rem] text-ink-3">
                        {describeSlot(slot)}
                        {slot.priority === 0
                          ? ""
                          : ` · p${String(slot.priority)}`}
                        {slot.enabled ? "" : " · off"}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Panel className="p-4">
            <h2 className="text-sm font-semibold text-ink">Feeds using this</h2>
            <ul className="mt-3 flex flex-col gap-3">
              {(feeds.data ?? []).map((feed) => (
                <li key={feed.id}>
                  <Switch
                    checked={feed.scheduleId === schedule.id}
                    disabled={assigning !== null || !schedule.enabled}
                    label={feed.name}
                    onChange={(uses) => {
                      assign(feed.id, uses);
                    }}
                    {...(feed.scheduleId === null ||
                    feed.scheduleId === schedule.id
                      ? {}
                      : { description: "Assigned another schedule" })}
                  />
                </li>
              ))}
            </ul>
            {(feeds.data ?? []).length === 0 ? (
              <p className="mt-3 text-xs text-ink-3">No feed exists yet.</p>
            ) : null}
            {schedule.enabled ? null : (
              <p className="mt-3 text-xs text-ink-3">
                A feed can only be assigned an enabled schedule.
              </p>
            )}
          </Panel>

          <Panel className="p-4">
            <h2 className="text-sm font-semibold text-ink">Assigned to</h2>
            <p className="mt-2 text-xs text-ink-3">
              {assigned.length === 0
                ? "No feed yet."
                : `${String(assigned.length)} ${assigned.length === 1 ? "feed" : "feeds"}, all reading these hours in ${schedule.timezone}.`}
            </p>
          </Panel>
        </div>
      </div>

      <SlotDialog
        onClose={() => {
          setDialogOpen(false);
        }}
        onDelete={(slotId) => {
          persist(slots.filter((slot) => slot.id !== slotId));
        }}
        onSave={saveSlot}
        open={dialogOpen}
        playlists={playlists.data ?? []}
        slot={editing}
      />
    </div>
  );

  function openSlot(slotId: string) {
    const found = slots.find((slot) => slot.id === slotId);
    if (!found) return;
    setEditing({
      id: found.id,
      playlistId: found.playlistId,
      daysOfWeek: found.daysOfWeek,
      startsAt: trimTime(found.startsAt),
      endsAt: trimTime(found.endsAt),
      priority: found.priority,
      enabled: found.enabled,
    });
    setDialogOpen(true);
  }
}

function secondsToInput(seconds: number): string {
  const whole = ((seconds % 86_400) + 86_400) % 86_400;
  return `${String(Math.floor(whole / 3_600)).padStart(2, "0")}:${String(
    Math.floor((whole % 3_600) / 60),
  ).padStart(2, "0")}`;
}

/**
 * Where the schedule's own clock is now — its weekday and how far into its day — read
 * in the schedule's timezone rather than the browser's, because that is the clock the
 * brain reads these hours by.
 */
function localNow(
  instant: number,
  timeZone: string,
): { day: number; seconds: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(instant));
    const read = (type: string) =>
      parts.find((part) => part.type === type)?.value ?? "";
    const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(
      read("weekday"),
    );
    if (day < 0) return null;
    return {
      day,
      seconds:
        Number(read("hour")) * 3_600 +
        Number(read("minute")) * 60 +
        Number(read("second")),
    };
  } catch {
    return null;
  }
}
